import { PastOrderPageRouterQuery } from '@portal/processes/PastOrderPageV2/types';
import { ShopUrlQuery } from '@portal/processes/ShopV2/ShopV2.types';
import { ApiMembersCheckEmailParameters } from 'api/members/check-email/types';
import { CreateAccountActivationUrlQuery } from 'api/members/createAccountActivationUrl/createAccountActivationUrl.schemas';
import { CreateAccountActivationUrlFromCustomerIdQuery } from 'api/members/createAccountActivationUrlFromCustomerId/createAccountActivationUrlFromCustomerId.schemas';
import { CreateCustomerParameters } from 'api/members/customer-create/types';
import {
  ApiGetCustomerMetafieldsParameters,
  ApiGetCustomerMetafieldsQuery
} from 'api/members/get-customer-metafields/types';
import { ApiMembersReferFriendParameters } from 'api/members/refer-friend/types';
import { SendInviteParameters } from 'api/members/sendInvite/sendInvite.types';
import { SetCustomerNotificationMetafieldsParameters } from 'api/members/set-customer-notification-metafields/types';
import {
  ApiGetCustomerByIDShopifyBodyParameters,
  ApiGetCustomerByIDShopifyParameters
} from 'api/shopify/customer-by-id/types';
import {
  ApiGetShopifyAddressesBodyParameters,
  ApiGetShopifyAddressesParameters
} from 'api/shopify/get-addresses/types';
import {
  ApiGetProductByIDShopifyBodyParameters,
  ApiGetProductByIDShopifyParameters
} from 'api/shopify/product-by-id/types';
import { ApiGetProductsShopifyParameters } from 'api/shopify/products/types';
import { WelcomeKitMetafieldsCheckPath } from 'api/shopify/welcomeKitMetafieldsCheck/welcomeKitMetafieldsCheck.types';
import { WelcomeKitMetafieldsUpdatePath } from 'api/shopify/welcomeKitMetafieldsUpdate/welcomeKitMetafieldsUpdate.types';
import { LoginUrlQuery } from 'processes/Login/types';
import { ISOLocalesEnum } from 'schema/locale';
import { StoreCode } from 'schema/storeCode';
import { ApiMembersGetCustomerAccountStateMetafieldsParametersSchema } from '../../api/members/get-customer-account-state-metafields/types';
import { ApiMembersAccountSecurityMetafieldsParametersSchema } from '../../api/members/set-customer-account-security-metafields/types';
import {
  createParametrizedUrl,
  createParametrizedUrlWithQueryString,
  createUrlWithBackUrl,
  withQueryString
} from './utils';

type UrlParam = string;

type StoreLangParams = {
  store: StoreCode;
  language: ISOLocalesEnum;
};

export const routes = {
  websiteRoot: '/',
  root: '/members',
  home: '/members/subscriptions',
  shop: withQueryString<ShopUrlQuery>('/members/shop'),
  shopPlp: '/shop',
  ordersHistory: '/members/orders',
  settings: '/members/settings',
  refFriend: '/members/refer-a-friend',
  orderDetail: createParametrizedUrlWithQueryString<{ id: UrlParam }, PastOrderPageRouterQuery>(
    '/members/orders/:id'
  ),
  subscriptionDetail: createParametrizedUrl<{ id: UrlParam }>('/members/subscriptions/:id'),
  rebill: createParametrizedUrl<{ id: UrlParam }>('/members/rebill/:id'),
  rebillConfirm: '/members/rebill/confirm',
  reactivate: createParametrizedUrl<{ id: UrlParam }>('/members/reactivate/:id'),
  checkoutShipment: '/checkout/shipment',
  checkoutPayment: '/checkout/payment',
  checkoutThanks: createParametrizedUrlWithQueryString<{ id: UrlParam }, { token: string }>(
    '/checkout/thanks/:id'
  ),
  offers: '/#offers',
  login: createUrlWithBackUrl('/members/login'),
  reset: withQueryString<LoginUrlQuery>('/members/reset'),
  activate: withQueryString<LoginUrlQuery>('/members/activate'),
  faq: '/about-ag1/faq',
  customerService: '/customer-service',
  blog: '/blog',
  nextGeneration: '/next-generation',
  blogCategory: createParametrizedUrl<{ slug: UrlParam }>('/blog/category/:slug'),
  blogPost: createParametrizedUrl<{ slug: UrlParam }>('/blog/post/:slug'),
  blogTag: createParametrizedUrl<{ slug: UrlParam }>('/blog/tag/:slug'),
  eduHub: '/blog',
  eduHubCategory: createParametrizedUrl<{ slug: UrlParam }>('/blog/category/:slug'),
  eduHubTag: createParametrizedUrl<{ slug: UrlParam }>('/blog/tag/:slug'),
  eduHubPost: createParametrizedUrl<{ slug: UrlParam }>('/blog/post/:slug'),
  page404: '/404',
  buildBundleUS: '/build-a-bundle',
  buildBundleShopify: '/build-a-bundle-shop',
  buildBundleShopifyV1: '/build-a-bundle-shop-v1',
  buildBundleShopifyV4: '/build-a-bundle-shop-v4',
  buildBundleEUorUk: '/homepage',
  buildBundleStep2: '/build-a-bundle-shop?step=2',
  buildBundleStep2DoubleSub: '/build-a-bundle-shop?step=2&type=double',
  buildBundleStep2TravelPack: '/build-a-bundle-shop?step=2&product=travel-packs',
  buildBundleStep2TravelPackDoubleSub:
    '/build-a-bundle-shop?step=2&product=travel-packs&type=double',
  quiz: '/about-ag1/how-ag1-works/ag1-for-you-quiz',
  directCheckout: createParametrizedUrl<{ id: UrlParam }>('/direct/:id')
};

export const apiRoutes = {
  aftership: {
    tracking: createParametrizedUrl<{ id: string; store: StoreCode }>(
      '/api/aftership/:store/trackings/:id'
    )
  },
  klaviyo: {
    addEvent: createParametrizedUrl<StoreLangParams>(
      '/api/members/klaviyo/:store/:language/add-event'
    ),
    subscribe: createParametrizedUrl<StoreLangParams>(
      '/api/members/klaviyo/:store/:language/subscribe'
    ),
    emailSignUp: createParametrizedUrl<{ store: StoreCode }>(
      '/api/members/klaviyo/:store/email-signup'
    )
  },
  members: {
    identify: '/api/members/identify',
    loqateAddressVerified: '/api/members/loqate-address-verified',
    checkEmail: createParametrizedUrl<ApiMembersCheckEmailParameters>(
      '/api/members/:store/check-email'
    ),
    getCustomerAccountStateMetafields:
      createParametrizedUrl<ApiMembersGetCustomerAccountStateMetafieldsParametersSchema>(
        '/api/members/:store/get-customer-account-state-metafields'
      ),
    referFriends: createParametrizedUrl<ApiMembersReferFriendParameters>(
      '/api/members/:store/refer-friend'
    ),
    getCustomer: createParametrizedUrl<{ store: StoreCode }>('/api/members/:store/customer'),
    getCustomerMetafields: createParametrizedUrlWithQueryString<
      ApiGetCustomerMetafieldsParameters,
      ApiGetCustomerMetafieldsQuery
    >('/api/members/:store/customer-metafields'),
    customerUpdate: createParametrizedUrl<{ store: StoreCode }>(
      '/api/members/:store/customer-update'
    ),
    customerCreate: createParametrizedUrl<CreateCustomerParameters>(
      '/api/members/:store/customer-create'
    ),
    getProducts: createParametrizedUrl<ApiGetProductsShopifyParameters>(
      '/api/members/shopify/:store/products'
    ),
    getProductByID: createParametrizedUrlWithQueryString<
      ApiGetProductByIDShopifyParameters,
      ApiGetProductByIDShopifyBodyParameters
    >('/api/members/shopify/:store/product-by-id'),
    getCustomerById: createParametrizedUrlWithQueryString<
      ApiGetCustomerByIDShopifyParameters,
      ApiGetCustomerByIDShopifyBodyParameters
    >('/api/members/shopify/:store/customer-by-id'),
    createFreeGiftDiscountCode: '/api/members/shopify/free-gift-discount-codes',
    createAccountActivationUrl: createParametrizedUrl<CreateAccountActivationUrlQuery>(
      '/api/members/:store/create-account-activation-url'
    ),
    createAccountActivationUrlFromCustomerId:
      createParametrizedUrl<CreateAccountActivationUrlFromCustomerIdQuery>(
        '/api/members/:store/create-account-activation-url-from-customer-id'
      ),
    setCustomerAccountSecurityMetafields:
      createParametrizedUrl<ApiMembersAccountSecurityMetafieldsParametersSchema>(
        '/api/members/:store/set-customer-account-security-metafields'
      ),
    welcomeKitMetafieldsCheck: createParametrizedUrl<WelcomeKitMetafieldsCheckPath>(
      '/api/members/:store/welcome-kit-metafields-check'
    ),
    welcomeKitMetafieldsUpdate: createParametrizedUrl<WelcomeKitMetafieldsUpdatePath>(
      '/api/members/:store/welcome-kit-metafields-update'
    ),
    sendInvite: createParametrizedUrl<SendInviteParameters>('/api/members/:store/send-invite'),
    getShippingMethods: createParametrizedUrlWithQueryString<
      { store: StoreCode },
      { country: string }
    >('/api/members/shopify/:store/get-shipping-methods'),
    getCountries: createParametrizedUrl<{ store: StoreCode }>(
      '/api/members/shopify/:store/get-countries'
    ),
    getAddresses: createParametrizedUrlWithQueryString<
      ApiGetShopifyAddressesParameters,
      ApiGetShopifyAddressesBodyParameters
    >('/api/members/shopify/:store/get-addresses'),
    sendPaymentChangeEmail: createParametrizedUrlWithQueryString(
      '/api/members/shopify/:store/send-payment-change-email'
    ),
    setCustomerNotificationMetafields:
      createParametrizedUrl<SetCustomerNotificationMetafieldsParameters>(
        '/api/members/:store/set-customer-notification-metafields'
      )
  },
  offer: {
    addDiscountCodesToCart: createParametrizedUrl<{ store: StoreCode }>(
      '/api/offer/:store/cart-discount-codes-update'
    ),
    createBasicLineItemDiscount: createParametrizedUrl<{ store: StoreCode }>(
      '/api/offer/:store/create-basic-discount'
    )
  }
};
